/**
 * 紙に流し込むデータ（PaperData）の作り方。
 *   ・明細の行は、いまの紙の「1枚に続けて（flat）」と同じ並び：内訳ごとに 見出しの行 → 行 → 「○○ 計」 → 最後に表紙の行。
 *     No. は金額の付く行にだけ振る（quote-html.ts の lineRow と同じ）
 *   ・項目（fields）は API が 見積・請求書・会社 から作る（fieldsOf の入口）。テスト印刷は見本（sampleData）
 */
import { TAX_MSG, hasReducedLine, taxNameMark, taxRowsInOrder, type EstimateLine, type PaperBind, type PaperData, type PaperKind, type PaperRow, type Product, type TaxSummary } from "@genbaryoku/contract";
import { jdate, pct, qty, yen } from "./fmt.js";

export type SheetLike = { id: string; type: "cover" | "breakdown"; name: string; subtotal: number; note: string; inUse?: boolean };

const row = (kind: PaperRow["kind"], p: Partial<PaperRow>): PaperRow => ({ kind, no: "", name: "", spec: "", qty: "", unit: "", price: "", amount: "", notes: "", ...p });

/** 1行 → 紙の行（No. は金額の付く行にだけ）。T2：金額の付く行の名称の後ろに、軽減は「 ※」・立替金は「（立替金）」（プリセットの紙と同じ） */
export function rowOfLine(l: EstimateLine, no: () => string): PaperRow {
  const mark = taxNameMark(l.taxCategory);
  switch (l.role) {
    case "group":
      return row("heading", { name: l.name });
    case "note":
      return row("text", { name: l.name });
    case "subtotal":
      return row("subtotal", { name: l.name || "小計", amount: yen(l.amount) });
    case "overhead_rate":
      return row("detail", { no: no(), name: l.name + mark, spec: l.spec, price: pct(l.rate), amount: yen(l.amount), notes: l.note });
    case "excluded":
      return row("detail", { no: no(), name: l.name, spec: l.spec, qty: qty(l.quantity), unit: l.unit, amount: l.amountLabel || "別途", notes: l.note });
    case "discount":
      return row("detail", { no: no(), name: (l.name || "値引き") + mark, spec: l.spec, amount: yen(l.amount), notes: l.note });
    default:
      return row("detail", { no: no(), name: l.name + mark, spec: l.spec, qty: qty(l.quantity), unit: l.unit, price: l.unitPrice == null ? "" : yen(l.unitPrice), amount: yen(l.amount), notes: l.note });
  }
}

/** 明細表の下の注記（T2）：軽減の行があれば「※は軽減税率対象」、無ければ空（PaperData.taxNote） */
export const taxNoteOf = (lines: ReadonlyArray<Pick<EstimateLine, "taxCategory">>): string => (hasReducedLine(lines) ? TAX_MSG.reducedNote : "");

/** 見積・請求書の行と内訳の紙 → 紙の行（1枚に続けての並び） */
export function rowsOf(lines: EstimateLine[], sheets: SheetLike[]): PaperRow[] {
  let n = 0;
  const no = () => String(++n);
  const cover = sheets.find((s) => s.type === "cover");
  // #199：不使用の内訳は紙に出さない（その行も）
  const breakdowns = sheets.filter((s) => s.type === "breakdown" && s.inUse !== false);
  const out: PaperRow[] = [];
  for (const s of breakdowns) {
    out.push(row("sheet", { name: s.name, notes: s.note }));
    for (const l of lines.filter((x) => x.sheetId === s.id)) out.push(rowOfLine(l, no));
    out.push(row("sheetTotal", { name: `${s.name}　計`, amount: yen(s.subtotal) }));
  }
  const coverLines = cover ? lines.filter((l) => l.sheetId === cover.id || l.sheetId == null) : lines;
  for (const l of coverLines) out.push(rowOfLine(l, no));
  return out;
}

export type TaxLike = Pick<TaxSummary, "kind" | "rate" | "reduced" | "taxableBase" | "taxAmount">;

/**
 * 税率ごとの内訳の文（1行に1税率）。適格請求書の④⑤。T1 から区分の文字でなく 課税の別・税率・軽減 から作る（標準だけの紙は今と同じ文字）。
 * T2：並びは 10% → 8% → 1% → 免税 → 非課税 → 不課税（taxRowsInOrder）。免税・非課税・不課税は「非課税 対象 ○円」（税額なし）。
 * 立替金は消費税の内訳に入れず、最後に「立替金 ○円」の1行（自分の書式の合計欄は 小計・消費税・合計 の3つだけなので、内訳の部品の下に置く）
 */
export function taxByRateText(taxes: TaxLike[]): string {
  const out = taxRowsInOrder(taxes).map((t) =>
    t.kind === "taxable"
      ? `${t.reduced ? `軽減 ${pct(t.rate)} 対象` : `${pct(t.rate)} 対象`} ${yen(t.taxableBase)}円　消費税 ${yen(t.taxAmount)}円`
      : `${t.kind === "export" ? "免税" : t.kind === "non_taxable" ? "非課税" : "不課税"} 対象 ${yen(t.taxableBase)}円`,
  );
  const reimb = taxes.filter((t) => t.kind === "reimbursement").reduce((a, t) => a + t.taxableBase, 0);
  if (reimb) out.push(`${TAX_MSG.reimbursement} ${yen(reimb)}円`);
  return out.join("\n");
}

export function emptyFields(): Record<PaperBind, string> {
  return {
    docNo: "", docDate: "", toName: "", toSub: "", projectName: "", site: "", period: "", outline: "", terms: "", valid: "", due: "", staff: "",
    coName: "", coRep: "", coAddr: "", coTel: "", coReg: "", coPerson: "",
    subtotal: "", tax: "", total: "", taxByRate: "", notes: "", bank: "", installment: "", pageNo: "",
    proviso: "", invoiceNo: "", docName: "",
    title: "", greet: "", receive: "", place: "", inspect: "", payOn: "", payMethod: "", clause: "", negotiation: "", ack: "",
  };
}

/** 見本の明細（製品ごと。#142 段6 No.36：営業力はログインの画面の小さな紙と同じ品目） */
const SAMPLE_LINES: Record<Product, Array<[string, string, number, string, number]>> = {
  genba: [
    ["足場仮設工事", "枠組足場・メッシュシート", 1, "式", 120000],
    ["高圧洗浄", "外壁・付帯部", 180, "㎡", 250],
    ["下地処理", "ひび割れ補修・シーリング打替", 1, "式", 45000],
    ["外壁塗装", "シリコン樹脂 3回塗り", 180, "㎡", 2800],
    ["付帯部塗装", "雨樋・破風・軒天", 1, "式", 85000],
    ["養生・清掃", "", 1, "式", 28000],
    ["諸経費", "", 1, "式", 38000],
  ],
  eigyo: [
    ["業務用プリンター A3対応", "型番 XP-3000", 1, "台", 298000],
    ["初期設定・搬入作業", "", 1, "式", 36000],
    ["保守サポート", "12か月", 12, "月", 6000],
    ["トナー（ブラック）", "", 4, "本", 8500],
    ["用紙 A4", "500枚×5冊", 10, "箱", 2400],
    ["操作説明", "オンライン 1時間", 1, "回", 15000],
    ["諸経費", "", 1, "式", 12000],
  ],
};

/** 「注文書」→「注　文　書」（4文字以下は字の間を空ける。{order}の紙の題と同じ） */
export const spacedTitle = (t: string): string => ([...t].length <= 4 ? [...t].join("\u3000") : t);

/**
 * {order}の条件の表の見出し（#233 2d。項目の head が刷る）。建設＝工事名・工事場所・工期・注文金額、仕入＝件名・納品場所・受領期日・合計金額
 * （pdf/order-html.ts の条件の表と同じ言葉）
 */
export function orderHeads(construction: boolean): Partial<Record<PaperBind, string>> {
  return construction
    ? { projectName: "工事名", place: "工事場所", receive: "工期", inspect: "検査完了期日", payOn: "支払期日", payMethod: "支払方法", total: "注文金額" }
    : { projectName: "件名", place: "納品場所", receive: "受領期日", inspect: "検査完了期日", payOn: "支払期日", payMethod: "支払方法", total: "合計金額" };
}

/** 署名欄（紙で受け取る請書）の行。代表者の欄の右に「印」 */
export const ACK_SIGN_ROWS: Array<[string, string]> = [["日付", "　　　　年　　　月　　　日"], ["住所", ""], ["会社名", ""], ["代表者", "印"]];

/** {order}の見本（#233 2d）。現場力＝建設の{order}（約款の一文と変更協議）、営業力＝仕入の{order}。兼請書の書式なら請書の欄（署名欄）も */
function orderSample(f: Record<PaperBind, string>, product: Product, combined: boolean, names: { order: string; orderAck: string }): Pick<PaperData, "heads" | "ack"> {
  const construction = product === "genba";
  Object.assign(f, {
    docNo: "PO-0034",
    title: spacedTitle(names.order),
    greet: "下記のとおり注文いたします。",
    toName: construction ? "株式会社 見本設備" : "株式会社 見本電機",
    toSub: "",
    projectName: construction ? "山田様邸 空調設備工事" : "業務用プリンター 仕入",
    receive: construction ? "2026年10月27日 〜 2026年11月14日" : "2026年10月27日",
    place: construction ? "大阪市北区中崎西2-3-4" : "大阪市北区梅田1-1-1（本社 倉庫）",
    inspect: "2026年11月21日",
    payOn: "2026年12月31日",
    payMethod: "銀行振込",
    due: "2026年12月31日",
    terms: "銀行振込",
    clause: construction ? `本${names.order}に記載のない事項は、添付の約款（建設工事標準下請契約約款）による。` : "",
    negotiation: construction ? "工期・請負代金の額の変更は、甲乙協議のうえ書面で定める。" : "",
    notes: "・納品時に納品書を添付してください",
  } satisfies Partial<Record<PaperBind, string>>);
  const heads = orderHeads(construction);
  if (!combined) return { heads };
  f.ack = "上記のとおりお請けいたします。";
  return {
    heads,
    ack: {
      head: { title: spacedTitle(names.orderAck), to: `${f.coName} 御中`, rows: [["番号", f.docNo], [heads.projectName!, f.projectName], [heads.total!, `¥${f.total}（税込）`]] },
      greet: "上記のとおりお請けいたします。",
      title: "請書（署名欄）",
      kind: "paper",
      rows: ACK_SIGN_ROWS,
    },
  };
}

/**
 * テスト印刷・エディタの見本（射場さんが試作で見た見本と同じ）。product は見本の中身（#142 段6 No.36。省けば現場力）。
 * {order}（#233 2d）は combined（兼請書の書式）と names（会社の言葉）で請書の欄と題が変わる
 */
export function sampleData(kind: PaperKind, rowCount = 7, product: Product = "genba", o: { combined?: boolean; names?: { order: string; orderAck: string } } = {}): PaperData {
  const lines = SAMPLE_LINES[product];
  const rows: PaperRow[] = [];
  let sub = 0;
  for (let i = 0; i < rowCount; i++) {
    const [name, spec, q, unit, price] = lines[i % lines.length]!;
    const amt = q * price;
    sub += amt;
    rows.push(row("detail", { no: String(i + 1), name: i >= lines.length ? `${name}（${Math.floor(i / lines.length) + 1}）` : name, spec, qty: qty(q), unit, price: yen(price), amount: yen(amt) }));
  }
  const tax = Math.floor(sub * 0.1);
  const f = emptyFields();
  Object.assign(f, {
    docNo: kind === "quote" ? "Q-0034" : kind === "invoice" ? "I-0012" : kind === "order" ? "PO-0034" : "R-0007", docDate: jdate("2026-09-24"), toName: "株式会社 山田建設 御中", toSub: "工事部 田中 様",
    projectName: "山田様邸 外壁塗装工事", site: "大阪市北区中崎西2-3-4", period: "2026年10月中旬〜11月上旬（約3週間）", outline: "外壁・付帯部の塗装一式",
    terms: "工事完了後 翌月末 お振込", valid: "発行日より30日", due: jdate("2026-10-31"), staff: "山本",
    coName: "株式会社 現場工務店", coRep: "代表取締役 現場 太郎", coAddr: "〒530-0001\n大阪市北区梅田1-1-1", coTel: "TEL 06-1234-5678　FAX 06-1234-5679", coReg: "登録番号 T1234567890123", coPerson: "担当：山本",
    subtotal: yen(sub), tax: yen(tax), total: yen(sub + tax), taxByRate: `10% 対象 ${yen(sub)}円　消費税 ${yen(tax)}円`,
    notes: kind === "quote" ? "・上記金額には消費税を含みます\n・お支払い条件：工事完了後 翌月末 お振込\n・有効期限を過ぎた場合は再見積となります" : "・お振込手数料はご負担ください\n・ご不明な点は担当までご連絡ください",
    bank: "〇〇銀行 梅田支店　普通 1234567　カ）ゲンバコウムテン", installment: "",
    proviso: "山田様邸 外壁塗装工事 代金として", invoiceNo: "No. I-0012",
  } satisfies Partial<Record<PaperBind, string>>);
  if (product === "eigyo") {
    Object.assign(f, {
      toName: "株式会社 山田商会 御中", toSub: "総務部 田中 様",
      projectName: "業務用プリンター 導入", site: "大阪市北区中崎西2-3-4（本社 3F）", period: "2026年10月下旬", outline: "プリンターの納品・設置・保守",
      terms: "納品後 翌月末 お振込",
      coName: "株式会社 サンプル商事", coRep: "代表取締役 見本 太郎",
      notes: kind === "quote" ? "・上記金額には消費税を含みます\n・お支払い条件：納品後 翌月末 お振込\n・有効期限を過ぎた場合は再見積となります" : "・お振込手数料はご負担ください\n・ご不明な点は担当までご連絡ください",
      bank: "〇〇銀行 梅田支店　普通 1234567　カ）サンプルシヨウジ",
      proviso: "業務用プリンター 導入 代金として",
    } satisfies Partial<Record<PaperBind, string>>);
  }
  if (kind === "receipt") {
    f.notes = "";
    return { kind, fields: f, rows, watermark: "" };
  }
  // 見積書・請求書（#217、新しい描き手）：宛名の敬称は分けて、合計は表の中、備考は表の下の枠、ページの下に帳票名
  const [name, hon] = splitHon(f.toName);
  const [toSub, hon2] = splitHon(f.toSub);
  f.toName = name;
  f.toSub = toSub;
  const names = o.names ?? { order: "注文書", orderAck: "注文請書" };
  const ord = kind === "order" ? orderSample(f, product, !!o.combined, names) : {};
  f.docName = `${kind === "quote" ? "見積書" : kind === "order" ? names.order : "請求書"} ${f.docNo}`;
  return {
    kind, fields: f, rows, watermark: "",
    ...ord,
    hon: kind === "order" ? "御中" : hon2 || hon,
    foot: [
      { kind: "sub", label: "小計", sub: "", amount: f.subtotal },
      { kind: "tax", label: "消費税（10%）", sub: `（対象 ${f.subtotal}円）`, amount: f.tax },
      { kind: "total", label: "合計（税込）", sub: "", amount: f.total },
    ],
    boxes: f.notes ? [{ title: "備考", text: f.notes }] : [],
  };
}

/** 「株式会社 山田建設 御中」→ ["株式会社 山田建設", "御中"]（見本の宛名の敬称を分ける） */
function splitHon(s: string): [string, string] {
  const m = /^(.*?)[ 　]+(御中|様|殿)$/.exec(s);
  return m ? [m[1]!, m[2]!] : [s, ""];
}

